import type React from "react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { MoreMenu } from "@/nzds/inputs/more-menu"
import { HStack } from "@/nzds/layout"

import { Box, MenuItem, Stack, Typography } from "@mui/material"

import {
  GRAPH_PNG_EXCLUDE_ATTR,
  useGraphToPng,
} from "../graph-to-png/graph-to-png"
import type { GraphMenuContainerProps } from "./graph-menu-container.types"
import MenuRefContext from "./graph-menu-ref-context"

/**
 * A component that wraps a graph (child) and provides a menu for exporting the graph as a PNG
 * For example:
 *  return (
 *    <GraphMenuContainer fileName={exportFileTitle}>
 *     <StackedAreaGraph
 *       colors={colors}
 *       data={data}
 *       keys={keys}
 *       xAxes={xAxes}
 *       yAxisLabel={yAxisLabel}
 *     />
 *    </GraphMenuContainer>
 *  )
 */

export const GraphMenuContainer: React.FC<GraphMenuContainerProps> = ({
  ariaLabel,
  captureElement = false,
  captureTestId,
  children,
  exportHiddenElements,
  extraItems,
  fileName,
  isExportDisabled = false,
  title,
}) => {
  const { t } = useTranslation("common")
  const [isExporting, setIsExporting] = useState(false)
  const isPngDisabled = isExportDisabled || isExporting
  const { ref, savePng } = useGraphToPng()
  const setCaptureElement = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node
    },
    [ref]
  )

  const handleExportPng = () => {
    if (isPngDisabled) {
      return
    }
    setIsExporting(true)
    savePng(fileName, exportHiddenElements)
      .catch((error: unknown) => {
        console.error(error)
      })
      .finally(() => {
        setIsExporting(false)
      })
  }

  const exportPngItem = (
    <MenuItem
      disabled={isPngDisabled}
      key="export-png"
      onClick={handleExportPng}
    >
      {t("menuItem.exportPng")}
    </MenuItem>
  )
  const overflowItems =
    extraItems == null ? (
      exportPngItem
    ) : (
      <>
        {extraItems}
        {exportPngItem}
      </>
    )
  const overflowMenu = (
    <Box
      sx={{ flexShrink: 0, ml: "auto" }}
      {...{ [GRAPH_PNG_EXCLUDE_ATTR]: "" }}
    >
      <MoreMenu
        ariaLabel={ariaLabel ?? t("pageTitle.secondaryActionsMenu")}
        disabled={isExportDisabled}
        moreMenuItems={overflowItems}
      />
    </Box>
  )
  const visualization = (
    <Stack
      data-testid={captureElement ? captureTestId : undefined}
      ref={captureElement ? setCaptureElement : undefined}
      spacing={1}
      sx={{ height: "100%", minWidth: 0, width: "100%" }}
    >
      <HStack
        sx={{
          alignItems: "center",
          flexShrink: 0,
          gap: 1,
          minWidth: 0,
          width: "100%",
        }}
      >
        {title == null ? null : (
          <Typography component="h3" sx={{ flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
        )}
        {overflowMenu}
      </HStack>
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%" }}>
        {children}
      </Box>
    </Stack>
  )

  return captureElement ? (
    visualization
  ) : (
    <MenuRefContext value={ref}>{visualization}</MenuRefContext>
  )
}
